/**
 * Particle Editor — full-screen modal overlay.
 * Three-column layout: effect list (left), preview (center), property editor (right).
 *
 * COMPOSITE MODEL: Thruster entries are grouped — selecting Normal or Afterburner shows
 * the flame+sparks composite preview (WYSIWYG). A sub-component dropdown switches which
 * config the property panel edits (flame vs sparks). Both configs are initialized as
 * draft overrides when any thruster entry is selected.
 *
 * DRAFT STATE ISOLATION: All slider/property changes only affect local draftConfig state
 * and draftOverrides. Live game effects are NOT modified until "Save Default" is clicked.
 *
 * "Export All Settings" exports postprocessing + particle selections + particle effect
 * tuning configs as a single JSON envelope.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useParticleEffectStore } from '../../stores/particleEffectStore.js';
import { particleDefaultsApi } from '../../api/client.js';
import { ParticleEditorPreview } from './ParticleEditorPreview.js';
import { ParticleEditorPropertyPanel } from './ParticleEditorPropertyPanel.js';
import type { ThrusterMode } from './ParticleEditorPropertyPanel.js';
import { exportAllSettings } from '../../stores/settingsExport.js';
import type {
  ParticleEffectId,
  ThrusterFlameConfig,
  ThrusterSparksConfig,
  AtmosphereWispsConfig,
} from '@warmancer/shared';
import { validateParticleEffectConfig } from '@warmancer/shared';
import styles from './ParticleEditor.module.css';

type EffectConfig = ThrusterFlameConfig | ThrusterSparksConfig | AtmosphereWispsConfig;

type SelectedGroup = 'thruster' | 'atmosphere_wisps';
type SubComponent = 'flame' | 'sparks';

interface ParticleEditorProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onBroadcastParticleUpdate: () => void;
}

/** Map sub-component to the underlying effect ID. */
function subComponentToEffectId(sub: SubComponent): ParticleEffectId {
  return sub === 'flame' ? 'thruster_flame' : 'thruster_sparks';
}

export const ParticleEditor = memo(function ParticleEditor({ visible, onClose, onBroadcastParticleUpdate }: ParticleEditorProps) {
  const [selectedGroup, setSelectedGroup] = useState<SelectedGroup>('thruster');
  const [selectedMode, setSelectedMode] = useState<ThrusterMode>('normal');
  const [selectedSubComponent, setSelectedSubComponent] = useState<SubComponent>('flame');
  const [playing, setPlaying] = useState(true);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [draftConfig, setDraftConfig] = useState<EffectConfig | undefined>(undefined);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The effect ID that the property panel currently edits
  const activeEffectId: ParticleEffectId = selectedGroup === 'thruster'
    ? subComponentToEffectId(selectedSubComponent)
    : 'atmosphere_wisps';

  // Whether we're in afterburner mode for preview
  const isAfterburner = selectedGroup === 'thruster' && selectedMode === 'afterburner';

  // Snapshot store config into draft when editor opens, group/mode/subcomponent changes.
  // For thruster entries, initializes BOTH flame and sparks drafts so composite preview works.
  useEffect(() => {
    if (!visible) {
      const store = useParticleEffectStore.getState();
      store.clearDraftOverrides();
      store.setSelectedPreviewEffect(null);
      return;
    }
    const store = useParticleEffectStore.getState();

    if (selectedGroup === 'thruster') {
      // Always set preview to thruster_flame (ThrusterExhaust renders both flame+sparks)
      store.setSelectedPreviewEffect('thruster_flame');

      // Initialize BOTH flame and sparks draft overrides for composite preview
      const flameDraft = (store.draftOverrides['thruster_flame'] ?? store.effects['thruster_flame']) as EffectConfig | undefined;
      if (flameDraft) {
        store.setDraftOverride('thruster_flame', { ...flameDraft });
      }
      const sparksDraft = (store.draftOverrides['thruster_sparks'] ?? store.effects['thruster_sparks']) as EffectConfig | undefined;
      if (sparksDraft) {
        store.setDraftOverride('thruster_sparks', { ...sparksDraft });
      }

      // Set the property panel's draft config to whichever sub-component is selected
      const activeDraft = (store.draftOverrides[activeEffectId] ?? store.effects[activeEffectId]) as EffectConfig | undefined;
      if (activeDraft) {
        setDraftConfig({ ...activeDraft });
      }
    } else {
      // Atmosphere
      store.setSelectedPreviewEffect('atmosphere_wisps');
      const storeConfig = (store.draftOverrides['atmosphere_wisps'] ?? store.effects['atmosphere_wisps']) as EffectConfig | undefined;
      if (storeConfig) {
        const draft = { ...storeConfig };
        setDraftConfig(draft);
        store.setDraftOverride('atmosphere_wisps', draft);
      }
    }
  }, [visible, selectedGroup, selectedMode, selectedSubComponent, activeEffectId]);

  // Close on Escape
  useEffect(() => {
    if (!visible) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [visible, onClose]);

  // Clear status messages after 3s
  useEffect(() => {
    if (!statusMsg) return;
    const timer = setTimeout(() => setStatusMsg(null), 3000);
    return () => clearTimeout(timer);
  }, [statusMsg]);

  useEffect(() => {
    if (!errorMsg) return;
    const timer = setTimeout(() => setErrorMsg(null), 5000);
    return () => clearTimeout(timer);
  }, [errorMsg]);

  const handleConfigChange = useCallback((partial: Partial<EffectConfig>) => {
    // Read current config from store (always fresh via getState, not stale React state)
    const store = useParticleEffectStore.getState();
    const current = (store.draftOverrides[activeEffectId] ?? store.effects[activeEffectId]) as EffectConfig | undefined;
    if (!current) return;
    const updated = { ...current, ...partial } as EffectConfig;
    // Write to draftOverrides OUTSIDE React setState updater (updaters must be pure in React 18)
    store.setDraftOverride(activeEffectId, updated);
    // Update local React state for slider display only
    setDraftConfig(updated);
  }, [activeEffectId]);

  const handleExport = useCallback(() => {
    const store = useParticleEffectStore.getState();
    if (selectedGroup === 'thruster') {
      // Export composite: both flame and sparks configs
      const flame = store.draftOverrides['thruster_flame'] ?? store.effects['thruster_flame'];
      const sparks = store.draftOverrides['thruster_sparks'] ?? store.effects['thruster_sparks'];
      const envelope = { flame, sparks };
      const json = JSON.stringify(envelope, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `thruster_${selectedMode}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      // Atmosphere: single config
      if (!draftConfig) return;
      const json = JSON.stringify(draftConfig, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'atmosphere_wisps.json';
      a.click();
      URL.revokeObjectURL(url);
    }
    setStatusMsg('Exported');
  }, [selectedGroup, selectedMode, draftConfig]);

  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        const store = useParticleEffectStore.getState();

        if (selectedGroup === 'thruster') {
          // Expect { flame, sparks } envelope
          if (!parsed || typeof parsed !== 'object' || !parsed.flame || !parsed.sparks) {
            setErrorMsg('Expected { flame, sparks } format');
            return;
          }
          const flameError = validateParticleEffectConfig('thruster_flame', parsed.flame);
          if (flameError) { setErrorMsg(`Flame: ${flameError}`); return; }
          const sparksError = validateParticleEffectConfig('thruster_sparks', parsed.sparks);
          if (sparksError) { setErrorMsg(`Sparks: ${sparksError}`); return; }

          store.setDraftOverride('thruster_flame', parsed.flame as EffectConfig);
          store.setDraftOverride('thruster_sparks', parsed.sparks as EffectConfig);
          // Update property panel to show the active sub-component
          const activeConfig = (activeEffectId === 'thruster_flame' ? parsed.flame : parsed.sparks) as EffectConfig;
          setDraftConfig(activeConfig);
        } else {
          // Atmosphere: single config
          const error = validateParticleEffectConfig('atmosphere_wisps', parsed);
          if (error) { setErrorMsg(error); return; }
          const imported = parsed as EffectConfig;
          setDraftConfig(imported);
          store.setDraftOverride('atmosphere_wisps', imported);
        }
        setStatusMsg('Imported');
      } catch {
        setErrorMsg('Invalid JSON');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, [selectedGroup, activeEffectId]);

  const handleReset = useCallback(() => {
    const store = useParticleEffectStore.getState();

    if (selectedGroup === 'thruster') {
      // Reset BOTH flame and sparks
      for (const id of ['thruster_flame', 'thruster_sparks'] as ParticleEffectId[]) {
        const serverDefault = store.serverDefaults?.[id];
        const resetConfig = serverDefault
          ? { ...serverDefault }
          : { ...(store.effects[id] as EffectConfig) };
        store.setDraftOverride(id, resetConfig);
      }
      // Update property panel
      const activeId = activeEffectId;
      const activeDraft = store.draftOverrides[activeId] as EffectConfig;
      setDraftConfig({ ...activeDraft });
    } else {
      const serverDefault = store.serverDefaults?.['atmosphere_wisps'];
      const resetConfig = serverDefault
        ? { ...serverDefault }
        : { ...(store.effects['atmosphere_wisps'] as EffectConfig) };
      setDraftConfig(resetConfig);
      store.setDraftOverride('atmosphere_wisps', resetConfig);
    }
    setStatusMsg('Reset to defaults');
  }, [selectedGroup, activeEffectId]);

  const handleSaveDefault = useCallback(() => {
    const store = useParticleEffectStore.getState();

    if (selectedGroup === 'thruster') {
      // Save BOTH flame and sparks
      const flameDraft = store.draftOverrides['thruster_flame'] as EffectConfig | undefined;
      const sparksDraft = store.draftOverrides['thruster_sparks'] as EffectConfig | undefined;
      if (!flameDraft || !sparksDraft) return;

      Promise.all([
        particleDefaultsApi.save('thruster_flame', flameDraft as unknown as Record<string, unknown>),
        particleDefaultsApi.save('thruster_sparks', sparksDraft as unknown as Record<string, unknown>),
      ]).then(() => {
        store.updateEffect('thruster_flame', flameDraft);
        store.updateEffect('thruster_sparks', sparksDraft);
        onBroadcastParticleUpdate();
        setStatusMsg('Default saved');
      }).catch((err: Error) => {
        setErrorMsg(err.message || 'Failed to save default');
      });
    } else {
      if (!draftConfig) return;
      particleDefaultsApi.save(
        'atmosphere_wisps',
        draftConfig as unknown as Record<string, unknown>,
      ).then(() => {
        store.updateEffect('atmosphere_wisps', draftConfig);
        onBroadcastParticleUpdate();
        setStatusMsg('Default saved');
      }).catch((err: Error) => {
        setErrorMsg(err.message || 'Failed to save default');
      });
    }
  }, [selectedGroup, draftConfig, onBroadcastParticleUpdate]);

  const handleExportAllSettings = useCallback(() => {
    const json = exportAllSettings();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'warmancer-settings.json';
    a.click();
    URL.revokeObjectURL(url);
    setStatusMsg('All settings exported');
  }, []);

  const handleSelectThruster = useCallback((mode: ThrusterMode) => {
    setSelectedGroup('thruster');
    setSelectedMode(mode);
  }, []);

  const handleSelectAtmosphere = useCallback(() => {
    setSelectedGroup('atmosphere_wisps');
  }, []);

  if (!visible) return null;

  const isThruster = selectedGroup === 'thruster';
  const currentMode: ThrusterMode | undefined = isThruster ? selectedMode : undefined;

  return (
    <div className={styles.overlay}>
      {/* Header */}
      <div className={styles.header}>
        <span className={styles.title}>Particle Editor</span>
        <button type="button" className={styles.closeButton} onClick={onClose}>&times;</button>
      </div>

      {/* Body: three-column layout */}
      <div className={styles.body}>
        {/* Left panel: composite effect list */}
        <div className={styles.leftPanel}>
          <div>
            <div className={styles.categoryLabel}>Thrusters</div>
            <button
              type="button"
              className={isThruster && selectedMode === 'normal' ? styles.effectItemActive : styles.effectItem}
              onClick={() => handleSelectThruster('normal')}
            >
              Normal
            </button>
            <button
              type="button"
              className={isThruster && selectedMode === 'afterburner' ? styles.effectItemActive : styles.effectItem}
              onClick={() => handleSelectThruster('afterburner')}
            >
              Afterburner
            </button>
          </div>
          <div>
            <div className={styles.categoryLabel}>Atmosphere</div>
            <button
              type="button"
              className={selectedGroup === 'atmosphere_wisps' ? styles.effectItemActive : styles.effectItem}
              onClick={handleSelectAtmosphere}
            >
              Atmosphere Wisps
            </button>
          </div>
        </div>

        {/* Center panel: composite preview */}
        <div className={styles.centerPanel}>
          <ParticleEditorPreview
            playing={playing}
            afterburner={isAfterburner}
          />
          <div className={styles.transportBar}>
            <button
              type="button"
              className={playing ? styles.transportButtonActive : styles.transportButton}
              onClick={() => setPlaying(!playing)}
            >
              {playing ? 'Pause' : 'Play'}
            </button>
          </div>
        </div>

        {/* Right panel: sub-component selector + property editor */}
        {draftConfig && (
          <div className={styles.rightPanelWrapper}>
            {isThruster && (
              <div className={styles.subComponentRow}>
                <span className={styles.subComponentLabel}>Component</span>
                <select
                  className={styles.subComponentSelect}
                  value={selectedSubComponent}
                  onChange={(e) => setSelectedSubComponent(e.target.value as SubComponent)}
                >
                  <option value="flame">Flame</option>
                  <option value="sparks">Sparks</option>
                </select>
              </div>
            )}
            <ParticleEditorPropertyPanel
              effectId={activeEffectId}
              config={draftConfig}
              onChange={handleConfigChange}
              mode={currentMode}
            />
          </div>
        )}
      </div>

      {/* Bottom action bar */}
      <div className={styles.actionBar}>
        <button type="button" className={styles.actionButton} onClick={handleExport}>
          Export
        </button>
        <button type="button" className={styles.actionButton} onClick={handleImportClick}>
          Import
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          className={styles.importInput}
          onChange={handleFileChange}
        />
        <button type="button" className={styles.actionButton} onClick={handleReset}>
          Reset
        </button>
        <button type="button" className={styles.actionButton} onClick={handleExportAllSettings}>
          Export All Settings
        </button>
        <div className={styles.actionSpacer} />
        <button type="button" className={styles.actionButtonPrimary} onClick={handleSaveDefault}>
          Save Default
        </button>
        {statusMsg && <span className={styles.statusMessage}>{statusMsg}</span>}
        {errorMsg && <span className={styles.errorMessage}>{errorMsg}</span>}
      </div>
    </div>
  );
});
